// IndexedDB CRUD untuk project, asset (V1.1), settings.

const DB_NAME = 'tm-studio'
const DB_VERSION = 1
const QUOTA_BYTES = 50 * 1024 * 1024 // 50 MB per Doc 09 §17.7

const STORES = {
  PROJECTS: 'projects',
  ASSETS: 'assets',
  SETTINGS: 'settings',
}

let dbPromise = null

function openDB() {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORES.PROJECTS)) {
        db.createObjectStore(STORES.PROJECTS, { keyPath: 'id' })
      }
      if (!db.objectStoreNames.contains(STORES.ASSETS)) {
        db.createObjectStore(STORES.ASSETS, { keyPath: 'key' })
      }
      if (!db.objectStoreNames.contains(STORES.SETTINGS)) {
        db.createObjectStore(STORES.SETTINGS, { keyPath: 'key' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(new Error(`IndexedDB open failed: ${req.error?.message}`))
  })
  return dbPromise
}

function tx(storeName, mode = 'readonly') {
  return openDB().then((db) => db.transaction(storeName, mode).objectStore(storeName))
}

function reqToPromise(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(new Error(`IDB request failed: ${req.error?.message}`))
  })
}

async function checkQuota(estimatedBytes) {
  if (!navigator.storage?.estimate) return true
  const { usage = 0, quota = QUOTA_BYTES } = await navigator.storage.estimate()
  return usage + estimatedBytes < quota
}

export async function saveProject(project) {
  if (!project.id) throw new Error('Project.id is required')
  const estimated = JSON.stringify(project).length * 2 // rough estimate
  if (!(await checkQuota(estimated))) {
    throw new Error('Penyimpanan penuh. Hapus proyek lama untuk menyimpan yang baru.')
  }
  const store = await tx(STORES.PROJECTS, 'readwrite')
  return reqToPromise(store.put(project))
}

export async function loadProject(id) {
  const store = await tx(STORES.PROJECTS)
  return reqToPromise(store.get(id))
}

export async function listProjects() {
  const store = await tx(STORES.PROJECTS)
  const projects = await reqToPromise(store.getAll())
  return projects.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
}

export async function deleteProject(id) {
  const store = await tx(STORES.PROJECTS, 'readwrite')
  return reqToPromise(store.delete(id))
}

export async function saveAsset(key, dataUrl) {
  const store = await tx(STORES.ASSETS, 'readwrite')
  return reqToPromise(store.put({ key, dataUrl, type: 'image/svg+xml' }))
}

export async function loadAsset(key) {
  const store = await tx(STORES.ASSETS)
  return reqToPromise(store.get(key))
}

export async function getSetting(key) {
  const store = await tx(STORES.SETTINGS)
  const result = await reqToPromise(store.get(key))
  return result?.value
}

export async function setSetting(key, value) {
  const store = await tx(STORES.SETTINGS, 'readwrite')
  return reqToPromise(store.put({ key, value }))
}

export async function getStorageUsage() {
  if (!navigator.storage?.estimate) return { usage: 0, quota: QUOTA_BYTES }
  return navigator.storage.estimate()
}

export async function _resetForTests() {
  const db = await openDB()
  db.close()
  dbPromise = null
  return new Promise((resolve, reject) => {
    const req = indexedDB.deleteDatabase(DB_NAME)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
}
